import React from "react";
import styled, { keyframes } from "styled-components";
import { useScrollReveal } from "../../hooks/useScrollReveal";

const Education: React.FC = () => {
  const { ref, isVisible } = useScrollReveal(0.1);

  return (
    <Section>
      <CosmicBg>
        <DataStream $x={20} />
        <DataStream $x={80} />
      </CosmicBg>

      <SectionInner ref={ref} $visible={isVisible}>
        <ThemeLabel>
          <ThemeDot /> Wizarding Examinations Authority
        </ThemeLabel>
        <SectionTag>MINISTRY ARCHIVE &middot; EXAMINATION RECORDS</SectionTag>
        <SectionTitle>O.W.L.s &amp; N.E.W.T.s</SectionTitle>

        <DataCard>
          <DataHeader>
            <DataCode>[CERTIFICATE &middot; I]</DataCode>
            <DataStatus>ATTESTED</DataStatus>
          </DataHeader>
          <DataTitle>Udacity React Nanodegree</DataTitle>
          <DataDesc>
            Certified React Developer — graduated from the Udacity Nanodegree
            Program. Comprehensive study of React, Redux, and React Native.
          </DataDesc>
          <DataLink
            href="https://confirm.udacity.com/QQN2KHS5"
            target="_blank"
            rel="noopener noreferrer"
          >
            &#9737; Verify Certificate &#8599;
          </DataLink>
        </DataCard>

        <LearningSection>
          <LearningTag>[ONGOING STUDY]</LearningTag>
          <LearningTitle>Currently Under Study</LearningTitle>
          <OrbGrid>
            {["GraphQL", "Gatsby", "Figma"].map((item) => (
              <LearningOrb key={item}>
                <OrbRing />
                <OrbLabel>{item}</OrbLabel>
              </LearningOrb>
            ))}
          </OrbGrid>
        </LearningSection>

        <ExaminerNote>
          EXAMINER&rsquo;S NOTE: &ldquo;These results have been examined and attested by the
          Wizarding Examinations Authority. Overall mark: Outstanding.&rdquo;
        </ExaminerNote>
      </SectionInner>
    </Section>
  );
};

export default Education;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const streamFlow = keyframes`
  0% { background-position: 0 0; }
  100% { background-position: 0 200px; }
`;

const orbRotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Section = styled.section`
  position: relative;
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 7rem 2rem;
  background: linear-gradient(180deg, #0a0a0a 0%, #040812 30%, #06101e 50%, #040812 70%, #0a0a0a 100%);
`;

const CosmicBg = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
`;

const DataStream = styled.div<{ $x: number }>`
  position: absolute;
  left: ${({ $x }) => $x}%;
  top: 0;
  bottom: 0;
  width: 1px;
  background: repeating-linear-gradient(
    180deg,
    transparent,
    transparent 10px,
    rgba(80, 140, 220, 0.03) 10px,
    rgba(80, 140, 220, 0.03) 12px
  );
  animation: ${streamFlow} 10s linear infinite;
`;

const SectionInner = styled.div<{ $visible: boolean }>`
  position: relative;
  z-index: 1;
  max-width: 700px;
  width: 100%;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  animation: ${({ $visible }) => ($visible ? fadeUp : "none")} 0.7s ease both;
`;

const ThemeLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: "Raleway", sans-serif;
  font-size: 0.6rem;
  color: #4a6a8a;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  margin-bottom: 1.5rem;
`;

const ThemeDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #508cdc;
  box-shadow: 0 0 8px rgba(80, 140, 220, 0.5);
`;

const SectionTag = styled.p`
  font-family: "Orbitron", monospace;
  font-size: 0.68rem;
  color: #508cdc;
  letter-spacing: 0.2em;
  margin-bottom: 0.5rem;
`;

const SectionTitle = styled.h2`
  font-family: "Orbitron", sans-serif;
  font-size: clamp(1.5rem, 3vw, 2.2rem);
  font-weight: 700;
  color: #c0d0e0;
  margin-bottom: 2rem;

  &::after {
    content: "";
    display: block;
    width: 60px;
    height: 1px;
    background: linear-gradient(90deg, #508cdc, transparent);
    margin-top: 0.8rem;
  }
`;

const DataCard = styled.div`
  padding: 1.5rem;
  background: rgba(4, 8, 18, 0.6);
  border: 1px solid rgba(80, 140, 220, 0.08);
  border-radius: 4px;
  margin-bottom: 2rem;
`;

const DataHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.6rem;
`;

const DataCode = styled.span`
  font-family: "Space Mono", monospace;
  font-size: 0.6rem;
  color: #508cdc;
  letter-spacing: 0.15em;
`;

const DataStatus = styled.span`
  font-family: "Space Mono", monospace;
  font-size: 0.55rem;
  color: #2ecc71;
  background: rgba(46, 204, 113, 0.06);
  padding: 0.15rem 0.5rem;
  border-radius: 2px;
  border: 1px solid rgba(46, 204, 113, 0.12);
`;

const DataTitle = styled.h3`
  font-family: "Orbitron", sans-serif;
  font-size: 1rem;
  color: #c0d0e0;
  font-weight: 600;
`;

const DataDesc = styled.p`
  font-family: "Raleway", sans-serif;
  font-size: 0.82rem;
  color: #7a9aaa;
  line-height: 1.7;
  margin: 0.5rem 0 1rem;
`;

const DataLink = styled.a`
  font-family: "Space Mono", monospace;
  font-size: 0.7rem;
  color: #508cdc;
  transition: all 0.25s ease;

  &:hover {
    color: #7ab0e8;
    text-shadow: 0 0 8px rgba(80, 140, 220, 0.3);
  }
`;

const LearningSection = styled.div`
  margin-bottom: 2rem;
`;

const LearningTag = styled.span`
  font-family: "Space Mono", monospace;
  font-size: 0.58rem;
  color: #508cdc;
  letter-spacing: 0.15em;
`;

const LearningTitle = styled.h3`
  font-family: "Orbitron", sans-serif;
  font-size: 0.82rem;
  color: #8aa0b8;
  margin: 0.3rem 0 1rem;
`;

const OrbGrid = styled.div`
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
`;

const LearningOrb = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
`;

const OrbRing = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 1px solid rgba(80, 140, 220, 0.15);
  border-top: 2px solid rgba(80, 140, 220, 0.4);
  animation: ${orbRotate} 6s linear infinite;
`;

const OrbLabel = styled.span`
  font-family: "Space Mono", monospace;
  font-size: 0.68rem;
  color: #8aa0b8;
`;

const ExaminerNote = styled.p`
  font-family: "Space Mono", monospace;
  font-size: 0.7rem;
  color: #3a5a6a;
  text-align: center;
  padding: 0.8rem;
  border: 1px solid rgba(80, 140, 220, 0.05);
  border-radius: 4px;
  background: rgba(4, 8, 18, 0.3);
`;
